/**
 * Matters client (LF-3215 / LF-3216)
 *
 * Browser-side helpers backed by `directApiClient`. Both reads (LF-3215)
 * and mutations (LF-3216) live here. Server-Component reads use the
 * `fetch*` helpers in `lib/data/matter-reads.ts` — do not import from
 * here in Server Components.
 *
 * Mutation function names + ActionResult shapes are preserved so existing
 * callers keep working without signature churn (LF-3214 lighter-touch
 * pattern). Read fetchers throw on error and return the unwrapped data.
 */

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import {
  Matter,
  MatterFilters,
  MatterListResponse,
  MatterSummary,
  CreateMatterRequest,
  UpdateMatterRequest,
  MatterActionResult,
  CreateMatterRequestSchema,
  UpdateMatterRequestSchema
} from './types';
import { formatDateForApi } from '@/lib/utils/date-format';

function toErrorResult<T>(error: unknown): MatterActionResult<T> {
  if (error instanceof DirectApiError) {
    return { success: false, error: error.message };
  }
  return {
    success: false,
    error: error instanceof Error ? error.message : 'Request failed'
  };
}

// ─── Reads (LF-3215) ────────────────────────────────────────────────────

interface MatterListBackendResponse {
  items: Array<Record<string, unknown>>;
  meta?: {
    totalItems?: number;
    itemCount?: number;
    itemsPerPage?: number;
    totalPages?: number;
    currentPage?: number;
  };
}

function transformMatterSummaries(
  items: Array<Record<string, unknown>>
): MatterSummary[] {
  return items.map((item) => {
    const assignedTo: string[] = [];
    if (item.actingSolicitor) {
      const solicitor = item.actingSolicitor as Record<string, unknown>;
      if (solicitor.firstName || solicitor.lastName) {
        const solicitorName =
          `${solicitor.firstName || ''} ${solicitor.lastName || ''}`.trim();
        assignedTo.push(solicitorName);
      } else if (solicitor.email) {
        assignedTo.push(String(solicitor.email));
      }
    }

    const lprNames = (
      (item.legalPersonalRepresentatives as Array<Record<string, unknown>>) ||
      []
    )
      .filter((lpr) => lpr.isLegalPersonalRepresentative)
      .map((lpr) =>
        [lpr.firstName, lpr.middleName, lpr.lastName].filter(Boolean).join(' ')
      )
      .filter(Boolean);

    return {
      id: item.id,
      type: item.type,
      typeLabel: item.typeLabel,
      lawFirmInternalReference: item.lawFirmInternalReference,
      decedentName:
        [item.decedentFirstName, item.decedentMiddleName, item.decedentLastName]
          .filter(Boolean)
          .join(' ') || 'Unknown',
      decedentFirstName: item.decedentFirstName,
      decedentMiddleName: item.decedentMiddleName,
      decedentLastName: item.decedentLastName,
      legalPersonalRepresentativeName: item.legalPersonalRepresentativeName,
      legalPersonalRepresentative: lprNames,
      legalPersonalRepresentatives: item.legalPersonalRepresentatives,
      dateOfDeath: item.decedentDateOfDeath,
      createdAt: item.createdAt,
      lastUpdated: item.updatedAt || item.createdAt,
      assignedTo,
      tasksOverdue: item.tasksOverdue || 0,
      progress: item.progress || 0,
      isArchived: item.isArchived || false,
      stages: item.stages,
      actingSolicitor: item.actingSolicitor,
      roles: item.roles,
      tags: item.tags,
      notes: item.notes,
      metadata: item.metadata,
      clients: item.clients
    } as MatterSummary;
  });
}

export async function fetchMatters(
  filters?: MatterFilters
): Promise<MatterListResponse> {
  const isAllMatters =
    !filters?.q &&
    !filters?.search &&
    !filters?.assignedToMe &&
    !filters?.hasTask &&
    filters?.includeArchived !== 'only';

  const params = new URLSearchParams();
  if (filters?.q) params.append('q', filters.q);
  if (filters?.search && !filters?.q) params.append('search', filters.search);
  if (filters?.includeArchived !== undefined)
    params.append('includeArchived', filters.includeArchived);
  if (filters?.assignedToMe !== undefined)
    params.append('assignedToMe', String(filters.assignedToMe));
  if (filters?.hasTask !== undefined)
    params.append('hasTask', String(filters.hasTask));
  if (filters?.page) params.append('page', String(filters.page));
  if (isAllMatters) {
    params.append('limit', String(100));
  } else if (filters?.limit) {
    params.append('limit', String(filters.limit));
  }
  if (filters?.sortBy) params.append('sortBy', filters.sortBy);
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters?.isDemo !== undefined)
    params.append('isDemo', String(filters.isDemo));

  const qs = params.toString();
  const response = await directApiClient.get<MatterListBackendResponse>(
    `/matters${qs ? `?${qs}` : ''}`
  );

  return {
    matters: transformMatterSummaries(response.items || []),
    total: response.meta?.totalItems || 0,
    page: response.meta?.currentPage || filters?.page || 1,
    limit: response.meta?.itemsPerPage || filters?.limit || 100,
    hasMore: response.meta
      ? (response.meta.currentPage ?? 0) < (response.meta.totalPages ?? 0)
      : false
  };
}

export async function fetchMatter(matterId: number): Promise<Matter> {
  return directApiClient.get<Matter>(`/matters/${matterId}`);
}

export async function fetchSuggestedReference(
  matterId?: number
): Promise<string> {
  const url = matterId
    ? `/matters/${matterId}/suggested-reference`
    : `/matters/suggested-reference`;

  const response = await directApiClient.get<{ suggestedReference: string }>(
    url
  );
  return response.suggestedReference;
}

// ─── Mutations ──────────────────────────────────────────────────────────

export async function createMatterAction(
  data: CreateMatterRequest
): Promise<MatterActionResult<Matter>> {
  try {
    const validationResult = CreateMatterRequestSchema.safeParse(data);
    if (!validationResult.success) {
      return {
        success: false,
        error: 'Validation failed',
        validationErrors: validationResult.error.flatten().fieldErrors
      };
    }

    const requestData = { ...validationResult.data };
    if (requestData.decedentDateOfDeath) {
      requestData.decedentDateOfDeath = formatDateForApi(
        requestData.decedentDateOfDeath
      );
    }

    const matter = await directApiClient.post<Matter>('/matters', requestData);
    return { success: true, data: matter };
  } catch (error) {
    return toErrorResult<Matter>(error);
  }
}

export async function updateMatterAction(
  matterId: number,
  data: UpdateMatterRequest
): Promise<MatterActionResult<Matter>> {
  try {
    const validationResult = UpdateMatterRequestSchema.safeParse(data);
    if (!validationResult.success) {
      return {
        success: false,
        error: 'Validation failed',
        validationErrors: validationResult.error.flatten().fieldErrors
      };
    }

    const requestData = { ...validationResult.data };
    if (requestData.decedentDateOfBirth) {
      requestData.decedentDateOfBirth = formatDateForApi(
        requestData.decedentDateOfBirth
      );
    }
    if (requestData.decedentDateOfDeath) {
      requestData.decedentDateOfDeath = formatDateForApi(
        requestData.decedentDateOfDeath
      );
    }
    if (requestData.willDate) {
      requestData.willDate = formatDateForApi(requestData.willDate);
    }

    const matter = await directApiClient.put<Matter>(
      `/matters/${matterId}`,
      requestData
    );
    return { success: true, data: matter };
  } catch (error) {
    return toErrorResult<Matter>(error);
  }
}

export async function deleteMatterAction(
  matterId: number
): Promise<MatterActionResult<void>> {
  try {
    await directApiClient.delete(`/matters/${matterId}`);
    return { success: true };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}

export async function archiveMatterAction(
  matterId: number
): Promise<MatterActionResult<Matter>> {
  return updateMatterAction(matterId, { isArchived: true });
}

export async function unarchiveMatterAction(
  matterId: number
): Promise<MatterActionResult<Matter>> {
  return updateMatterAction(matterId, { isArchived: false });
}

export async function createMatterWithTypeAction(
  payload: {
    type: string;
    metadata: { clientNames?: string[]; description?: string };
    lawFirmInternalReference?: string;
    notes?: string;
    isArchived: boolean;
  },
  scheduledDate?: string
): Promise<MatterActionResult<Matter>> {
  try {
    const queryParams = scheduledDate ? `?scheduledDate=${scheduledDate}` : '';

    const matter = await directApiClient.post<Matter>(
      `/matters${queryParams}`,
      payload
    );
    return { success: true, data: matter };
  } catch (error) {
    return toErrorResult<Matter>(error);
  }
}
